import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  ExternalLink,
  Loader2,
  Star,
  MessageSquare,
  Globe,
  MapPin,
  Calendar,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { getPropFirmById } from '@/services/propFirmApi';

const LOGO_BASE_URL = 'https://prop-api.8100081.xyz/prop-firms/firmlogo';

const getLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  if (
    logoUrl.startsWith('http://') ||
    logoUrl.startsWith('https://') ||
    logoUrl.startsWith('data:')
  ) {
    return logoUrl;
  }
  return `${LOGO_BASE_URL}/${logoUrl}`;
};

const formatPrice = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  return isNaN(num) ? value : `$${num.toLocaleString()}`;
};

export const PropFirmViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [firm, setFirm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;

    const load = async () => {
      try {
        setLoading(true);
        const res = await getPropFirmById(id);
        const data = res?.data || res;
        if (!data) {
          toast.error('Firm not found');
          navigate('/dashboard/firms');
          return;
        }
        setFirm(data);
      } catch (error) {
        console.error(error);
        toast.error('Failed to load firm');
        navigate('/dashboard/firms');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!firm) return null;

  const logo = getLogoUrl(firm.logo_url || firm.logo);
  const pros = (firm.pros_cons || []).filter((x) => x.type === 'pro');
  const cons = (firm.pros_cons || []).filter((x) => x.type === 'con');
  const allowed = (firm.features || []).filter((x) => x.type === 'allowed');
  const notAllowed = (firm.features || []).filter((x) => x.type === 'not_allowed');
  const challenges = firm.challenges || [];
  const platforms = firm.platforms || [];
  const instruments = firm.instruments || [];
  const paymentMethods = firm.payment_methods || firm.paymentMethods || [];
  const faqs = firm.faqs || [];
  const discounts = firm.discounts || firm.coupons || [];

  return (
    <div>
      <PageHeader
        title={firm.name || 'Prop Firm'}
        description={firm.tagline || firm.slug || 'Firm details'}
        breadcrumbs={['Admin', 'Content', 'Prop Firms', 'View']}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/dashboard/firms')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button onClick={() => navigate(`/dashboard/firms/${id}/edit`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main */}
        <div className="lg:col-span-2 space-y-6">
          {/* Overview */}
          <Card className="rounded-xl border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                {logo ? (
                  <img
                    src={logo}
                    alt={firm.name}
                    className="h-16 w-16 rounded-lg object-contain border border-slate-200 bg-white"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-lg bg-slate-200 flex items-center justify-center text-xl font-semibold text-slate-600">
                    {firm.name?.charAt(0) || '?'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-semibold text-slate-900">
                      {firm.name}
                    </h2>
                    <StatusBadge status={firm.status} />
                    {(firm.featured || firm.featured_order) && (
                      <Badge className="bg-amber-50 text-amber-700 border-amber-200">
                        Featured
                      </Badge>
                    )}
                  </div>
                  {firm.tagline && (
                    <p className="text-sm text-slate-600 mt-1">{firm.tagline}</p>
                  )}
                  <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-600">
                    {firm.country && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" />
                        {firm.country}
                      </span>
                    )}
                    {(firm.founded_year || firm.foundedYear) && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        Founded {firm.founded_year || firm.foundedYear}
                      </span>
                    )}
                    {(firm.website || firm.website_url) && (
                      <a
                        href={firm.website || firm.website_url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 text-blue-600 hover:underline"
                      >
                        <Globe className="h-3.5 w-3.5" />
                        Website
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {firm.description && (
                <>
                  <Separator className="my-4" />
                  <div
                    className="prose prose-sm max-w-none text-slate-700"
                    dangerouslySetInnerHTML={{ __html: firm.description }}
                  />
                </>
              )}
            </CardContent>
          </Card>

          {/* Challenges */}
          {challenges.length > 0 && (
            <Card className="rounded-xl border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Challenges</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {challenges.map((c, i) => (
                  <div
                    key={c.id || i}
                    className="p-4 rounded-lg border border-slate-200 bg-slate-50"
                  >
                    <p className="font-medium text-slate-900">
                      {c.name || `Challenge ${i + 1}`}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
                      <div>
                        <p className="text-xs text-slate-500">Price</p>
                        <p className="font-medium">{formatPrice(c.price)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Max Funding</p>
                        <p className="font-medium">
                          {formatPrice(c.max_funding ?? c.maxFunding)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Profit Split</p>
                        <p className="font-medium">
                          {c.profit_split ?? c.profitSplit ?? '—'}
                          {(c.profit_split ?? c.profitSplit) != null ? '%' : ''}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Frequency</p>
                        <p className="font-medium">{c.frequency || '—'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Pros & Cons */}
          {(pros.length > 0 || cons.length > 0) && (
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="rounded-xl border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg text-emerald-700">Pros</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {pros.map((p, i) => (
                      <li key={i} className="text-sm text-slate-700 flex gap-2">
                        <span className="text-emerald-600">+</span>
                        {p.text}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
              <Card className="rounded-xl border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg text-red-700">Cons</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {cons.map((c, i) => (
                      <li key={i} className="text-sm text-slate-700 flex gap-2">
                        <span className="text-red-600">−</span>
                        {c.text}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Features */}
          {(allowed.length > 0 || notAllowed.length > 0) && (
            <Card className="rounded-xl border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Features & Rules</CardTitle>
              </CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-slate-900 mb-2">Allowed</p>
                  <ul className="space-y-1">
                    {allowed.map((a, i) => (
                      <li key={i} className="text-sm text-slate-600">
                        • {a.text}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 mb-2">
                    Not Allowed
                  </p>
                  <ul className="space-y-1">
                    {notAllowed.map((a, i) => (
                      <li key={i} className="text-sm text-slate-600">
                        • {a.text}
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          )}

          {/* FAQs */}
          {faqs.length > 0 && (
            <Card className="rounded-xl border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">FAQs</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {faqs.map((f, i) => (
                  <div key={i}>
                    <p className="text-sm font-medium text-slate-900">
                      {f.question}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">{f.answer}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="rounded-xl border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Rating</span>
                <span className="flex items-center gap-1 font-medium">
                  <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                  {firm.avg_rating != null
                    ? Number(firm.avg_rating).toFixed(1)
                    : '—'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Reviews</span>
                <span className="flex items-center gap-1 font-medium">
                  <MessageSquare className="h-4 w-4 text-blue-500" />
                  {firm.review_count ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Starting price</span>
                <span className="font-medium">
                  {formatPrice(firm.min_starting_price)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Max funding</span>
                <span className="font-medium">
                  {formatPrice(firm.max_funding)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Slug</span>
                <code className="text-xs bg-slate-100 px-2 py-0.5 rounded">
                  {firm.slug || '—'}
                </code>
              </div>
            </CardContent>
          </Card>

          {(platforms.length > 0 ||
            instruments.length > 0 ||
            paymentMethods.length > 0) && (
            <Card className="rounded-xl border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Trading</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {platforms.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase mb-2">
                      Platforms
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {platforms.map((p, i) => (
                        <Badge key={i} variant="secondary">
                          {typeof p === 'string' ? p : p.value || p.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {instruments.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase mb-2">
                      Instruments
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {instruments.map((p, i) => (
                        <Badge key={i} variant="outline">
                          {typeof p === 'string' ? p : p.value || p.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {paymentMethods.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase mb-2">
                      Payment methods
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {paymentMethods.map((p, i) => (
                        <Badge key={i} variant="secondary">
                          {typeof p === 'string' ? p : p.value || p.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {discounts.length > 0 && (
            <Card className="rounded-xl border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Discounts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {discounts.map((d, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded"
                  >
                    <code className="text-xs bg-white px-2 py-0.5 rounded border">
                      {d.discount_code || d.code}
                    </code>
                    <span className="text-emerald-700 font-medium">
                      {d.discount_text || d.discount}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {(firm.affiliate_url || firm.affiliateUrl) && (
            <Button asChild className="w-full" variant="outline">
              <a
                href={firm.affiliate_url || firm.affiliateUrl}
                target="_blank"
                rel="noreferrer"
              >
                Affiliate link
                <ExternalLink className="h-4 w-4 ml-2" />
              </a>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};